import { createEffect, createMemo, createResource, type JSX, onCleanup, Show } from "solid-js"
import { useLocation } from "@solidjs/router"
import { useLanguage } from "@/context/language"
import { CoworkApiError, coworkAgentMode, getBootstrapMe, getRuntimeConnection } from "@/cowork/api"
import { CoworkAuthPage } from "@/cowork/auth-page"
import { useCoworkAuth } from "@/cowork/auth-store"
import { isPublicRoute, publicRegistrationEnabled } from "@/cowork/public-routes"
import { useServer } from "@/context/server"
import { authLoadingClass } from "@/cowork/auth-shell"
import { CoworkWorkspaceGate } from "@/cowork/workspace-gate"
import { activeCompanyID, recoverActiveCompanyID } from "@/cowork/local-auth"
export { activeCompanyID, localDevHeaders, setActiveCompanyID } from "@/cowork/local-auth"
export { CoworkAuthProvider, useCoworkAuth } from "@/cowork/auth-store"

export function CoworkAuthGate(props: { children: JSX.Element }) {
  const auth = useCoworkAuth()
  const language = useLanguage()
  const location = useLocation()
  const server = useServer()

  if (!coworkAgentMode()) return props.children

  const publicRoute = createMemo(() => isPublicRoute(location.pathname))

  let disposed = false
  onCleanup(() => {
    disposed = true
    server.setManagedRuntime(undefined)
  })
  createEffect(() => {
    if (!auth.authenticated()) server.setManagedRuntime(undefined)
  })

  const [runtimeReady, runtimeActions] = createResource(
    () => (publicRoute() ? undefined : auth.state.accessToken || undefined),
    async (token) => {
      const connection = await runtimeConnection()
      if (disposed || auth.state.accessToken !== token) return false
      server.setManagedRuntime({
        type: "http",
        displayName: connection.displayName || "Cowork Report Agent",
        http: {
          url: connection.url,
          username: connection.username || "opencode",
          password: connection.password,
        },
      })
      return true
    },
  )

  async function runtimeConnection() {
    try {
      return await getRuntimeConnection()
    } catch (error) {
      if (!(error instanceof CoworkApiError) || error.status !== 403) throw error
      const identity = await getBootstrapMe()
      if (!identity.company_id || identity.company_id === activeCompanyID()) throw error
      recoverActiveCompanyID(identity.company_id)
      return getRuntimeConnection(identity.company_id)
    }
  }

  return (
    <Show when={!publicRoute()} fallback={props.children}>
      <Show
        when={auth.authenticated()}
        fallback={<CoworkAuthPage registration={publicRegistrationEnabled() && location.pathname === "/register"} />}
      >
          <Show
            when={runtimeReady.state === "errored" ? false : runtimeReady()}
            fallback={
              <Show
                when={runtimeReady.error}
                fallback={<div data-cowork-auth class={authLoadingClass}>{language.t("cowork.login.loading")}</div>}
              >
                <Show
                  when={runtimeReady.error instanceof CoworkApiError && runtimeReady.error.status === 403}
                  fallback={
                    <div data-cowork-auth class={authLoadingClass} role="alert">
                      <p>{language.t("cowork.enterprise.scope.loadError")}</p>
                      <button type="button" onClick={() => void runtimeActions.refetch()}>
                        {language.t("cowork.enterprise.scope.retry")}
                      </button>
                    </div>
                  }
                >
                  <CoworkAuthPage registration={false} />
                </Show>
              </Show>
            }
          >
            <CoworkWorkspaceGate>{props.children}</CoworkWorkspaceGate>
          </Show>
      </Show>
    </Show>
  )
}
